import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ComunicacaoService,
  ComunicacaoHistoricoItem,
  HistoricoPagedResponse
} from 'src/app/services/comunicacao/comunicacao.service';
import { AlunoStoreSelectors, AlunoStoreState } from 'src/app/_store/aluno-store';

export const CANAL_MAP: { valor: number; label: string; icon: string; css: string }[] = [
  { valor: 1, label: 'WhatsApp',       icon: 'chat',  css: 'cf-wpp'   },
  { valor: 3, label: 'Email',          icon: 'email', css: 'cf-email' },
  { valor: 2, label: 'SMS',            icon: 'sms',   css: 'cf-sms'   },
  { valor: 4, label: 'Torpedo de Voz', icon: 'phone', css: 'cf-voice' },
];

@Component({
  selector: 'app-aluno-comunicacao',
  templateUrl: './aluno-comunicacao.component.html',
  styleUrls: ['./aluno-comunicacao.component.scss']
})
export class AlunoComunicacaoComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private alunoId: number;

  // State
  loading = false;
  error = false;

  // Dados
  mensagens: ComunicacaoHistoricoItem[] = [];
  totalItems = 0;

  // Paginação
  pageSize = 20;
  pageIndex = 0;
  pageSizeOptions = [10, 20, 50];

  // Filtros
  canalAtivo: number | null = null;
  buscaControl = new FormControl('');
  filtroTituloControl = new FormControl('');
  filtroRegraControl = new FormControl('');
  canais = CANAL_MAP;

  // Dados brutos e filtrados (filtroRegra é client-side)
  mensagensRaw: ComunicacaoHistoricoItem[] = [];

  displayedColumns = ['dataEnvio', 'canal', 'titulo', 'regraNome', 'tentativa', 'status'];

  constructor(
    private comunicacaoService: ComunicacaoService,
    private storeAluno: Store<AlunoStoreState.Aluno>
  ) {}

  ngOnInit(): void {
    // Lê do localStorage (fluxo padrão: initial.component seta antes de navegar)
    this.alunoId = Number(window.localStorage.getItem('alunoIdLocalStorage')) || 0;

    // Fallback: NgRx store (caso navegação direta ou refresh)
    this.storeAluno.pipe(
      select(AlunoStoreSelectors.selectCadastroAluno),
      takeUntil(this.destroy$)
    ).subscribe(val => {
      if (val?.id && !this.alunoId) {
        this.alunoId = Number(val.id);
        this.carregarHistorico();
      }
    });

    this.buscaControl.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => { this.pageIndex = 0; this.carregarHistorico(); });

    this.filtroTituloControl.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => { this.pageIndex = 0; this.carregarHistorico(); });

    this.filtroRegraControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.aplicarFiltroRegraLocal());

    if (this.alunoId) {
      this.carregarHistorico();
    } else {
      this.error = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  carregarHistorico(): void {
    if (!this.alunoId) {
      this.error = true;
      return;
    }

    this.loading = true;
    this.error = false;

    this.comunicacaoService.getHistoricoAluno(this.alunoId, {
      canal: this.canalAtivo,
      busca: this.filtroTituloControl.value || this.buscaControl.value || undefined,
      take: this.pageSize,
      skip: this.pageIndex * this.pageSize
    }).pipe(takeUntil(this.destroy$)).subscribe(
      (response: HistoricoPagedResponse) => {
        this.mensagensRaw = response.data || [];
        this.totalItems = response.total || 0;
        this.aplicarFiltroRegraLocal();
        this.loading = false;
      },
      () => {
        this.error = true;
        this.loading = false;
      }
    );
  }

  toggleCanal(valor: number): void {
    this.canalAtivo = this.canalAtivo === valor ? null : valor;
    this.pageIndex = 0;
    this.carregarHistorico();
  }

  aplicarFiltroRegraLocal(): void {
    const regra = (this.filtroRegraControl.value || '').toLowerCase().trim();
    this.mensagens = regra
      ? this.mensagensRaw.filter(m => (m.regraNome || '').toLowerCase().includes(regra))
      : this.mensagensRaw;
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.carregarHistorico();
  }

  getCanalLabel(canal: number): string {
    return CANAL_MAP.find(c => c.valor === canal)?.label || `Canal ${canal}`;
  }

  getCanalIcon(canal: number): string {
    return CANAL_MAP.find(c => c.valor === canal)?.icon || 'chat';
  }

  getCanalCss(canal: number): string {
    return CANAL_MAP.find(c => c.valor === canal)?.css || '';
  }
}
